/**
 * dateUtils.ts
 * Centrala hjälpfunktioner för datumformatering.
 *
 * Regel: Alla datum lagras i UTC i databaserna.
 *        Alla datum som visas för användaren ska visas i webbläsarens lokala tidszon.
 *
 * Använd ALLTID dessa funktioner istället för new Date().toISOString() / .toLocaleString() direkt.
 */

/** Webbläsarens lokala tidszon (automatisk) */
const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const HAS_TZ = /[zZ]$|[+-]\d{2}:\d{2}$/;
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Tolka API-/DB-tid som UTC. MariaDB DATETIME kommer ofta som
 * "2026-08-21 07:07:14" utan Z — new Date() skulle annars läsa det som lokal tid.
 */
export function parseApiUtcDate(value: Date | string | number): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const s = String(value).trim();
  if (!s) return new Date(Number.NaN);
  if (HAS_TZ.test(s)) return new Date(s);
  if (DATE_ONLY.test(s)) return new Date(`${s}T00:00:00`);
  if (NAIVE_DATETIME.test(s)) return new Date(`${s.replace(' ', 'T')}Z`);
  return new Date(s);
}

/**
 * Formatera ett datum till lokal datum och tid.
 * Exempel: "2026-04-06 08:45" (sv) eller "04/06/2026, 08:45 AM" (en)
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '–';
  const d = typeof date === 'string' ? parseApiUtcDate(date) : date;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: USER_TIMEZONE,
  }).format(d);
}

/**
 * Formatera ett datum till lokal datum (utan tid).
 * Exempel: "2026-04-06"
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '–';
  const d = typeof date === 'string' ? parseApiUtcDate(date) : date;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'short',
    timeZone: USER_TIMEZONE,
  }).format(d);
}

/**
 * Formatera ett datum till lokal tid (utan datum).
 * Exempel: "08:45"
 */
export function formatTime(date: Date | string | null | undefined): string {
  if (!date) return '–';
  const d = typeof date === 'string' ? parseApiUtcDate(date) : date;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat(navigator.language, {
    timeStyle: 'short',
    timeZone: USER_TIMEZONE,
  }).format(d);
}

/**
 * Formatera lokal tid med sekunder (t.ex. playback-slider).
 * Exempel: "08:45:12"
 */
export function formatTimeWithSeconds(date: Date | string | number | null | undefined): string {
  if (date == null) return '–';
  const d = typeof date === 'number'
    ? new Date(date)
    : typeof date === 'string'
      ? parseApiUtcDate(date)
      : date;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: USER_TIMEZONE,
  }).format(d);
}

/**
 * DATE-kolumn / ISO-datum → värde för `<input type="date">` (`YYYY-MM-DD`).
 * Tar kalenderdatumet i strängen (inte lokal tidszon) så att ISO från API inte
 * ger ogiltigt input-värde och sedan spräcker MariaDB DATE vid spara.
 */
export function toDateInputValue(value: Date | string | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Formatera Date till värde för DateTimeLocalInput (`type="datetime-local"`) i lokal tid.
 * Returnerar "YYYY-MM-DDTHH:mm".
 */
export function toDatetimeLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Lokal kalenderdag YYYY-MM-DD (inte UTC från toISOString). */
export function localYmd(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Tolka värde från DateTimeLocalInput (`type="datetime-local"`) som lokal tid.
 */
export function parseDatetimeLocalInput(value: string): Date {
  return new Date(value);
}

/**
 * Konvertera datetime-local (lokal väggklocka) till ISO 8601 UTC för API (…Z).
 */
export function datetimeLocalInputToUtcIso(value: string): string {
  const d = parseDatetimeLocalInput(value);
  if (Number.isNaN(d.getTime())) {
    throw new Error('Invalid datetime');
  }
  return d.toISOString();
}

const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/;

/**
 * Säkert: lokal YYYY-MM-DD + HH:mm → UTC ISO, eller null om ogiltigt.
 * Använd i render/useMemo – kasta aldrig (undvik vit skärm vid delvis datum-inmatning).
 */
export function localDateAndTimeToUtcIso(
  date: string | null | undefined,
  time: string | null | undefined,
): string | null {
  if (!date || !time || !LOCAL_DATE_RE.test(date) || !LOCAL_TIME_RE.test(time)) {
    return null;
  }
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  const d = new Date(`${date}T${normalizedTime}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Formatera ett datum till ISO-format men med lokal tidszon.
 * Returnerar "YYYY-MM-DD HH:mm" i lokal tid – bra för tabeller.
 */
export function formatDateTimeLocal(
  date: Date | string | null | undefined,
  options?: { seconds?: boolean },
): string {
  if (!date) return '–';
  const d = typeof date === 'string' ? parseApiUtcDate(date) : date;
  if (isNaN(d.getTime())) return '–';

  const fmt = new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(options?.seconds ? { second: '2-digit' as const } : {}),
    timeZone: USER_TIMEZONE,
  });
  return fmt.format(d).replace(',', '');
}

/**
 * Formatera ett datum till "YYYY-MM-DD" i lokal tidszon.
 */
export function formatDateISO(date: Date | string | null | undefined): string {
  if (!date) return '–';
  const d = typeof date === 'string' ? parseApiUtcDate(date) : date;
  if (isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: USER_TIMEZONE,
  }).format(d);
}

/**
 * Returnera lokalt midnatt (00:00:00) som Date för ett givet datum.
 * Används för att bygga datum-intervall som skickas till API.
 */
export function toLocalMidnight(date: Date): Date {
  const iso = new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: USER_TIMEZONE,
  }).format(date);
  // iso är "YYYY-MM-DD" i lokal tidszon – tolkas som lokal tid av JS
  return new Date(`${iso}T00:00:00`);
}

/**
 * Returnera antalet sekunder som lokal tidszon är förskjuten från UTC just nu.
 * Positivt = öster om UTC (t.ex. Sverige +7200 sommar).
 */
export function localOffsetSeconds(): number {
  return -(new Date().getTimezoneOffset() * 60);
}
